import React, { useState, useEffect, useCallback } from 'react';

interface Cell {
  isMine: boolean;
  isRevealed: boolean;
  isFlagged: boolean;
  neighborCount: number;
}

const Minesweeper: React.FC = () => {
  const ROWS = 9;
  const COLS = 9;
  const MINES = 10;

  const [board, setBoard] = useState<Cell[][]>([]);
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost'>('playing');
  const [mineCount, setMineCount] = useState(MINES);
  const [time, setTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  const initializeBoard = useCallback(() => {
    // Create empty board
    const newBoard: Cell[][] = Array(ROWS).fill(null).map(() =>
      Array(COLS).fill(null).map(() => ({
        isMine: false,
        isRevealed: false,
        isFlagged: false,
        neighborCount: 0,
      }))
    );

    // Place mines randomly
    const minePositions = new Set<string>();
    while (minePositions.size < MINES) {
      const row = Math.floor(Math.random() * ROWS);
      const col = Math.floor(Math.random() * COLS);
      minePositions.add(`${row}-${col}`);
    }

    minePositions.forEach(pos => {
      const [row, col] = pos.split('-').map(Number);
      newBoard[row][col].isMine = true;
    });

    // Calculate neighbor counts
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        if (!newBoard[row][col].isMine) {
          let count = 0;
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              const newRow = row + dr;
              const newCol = col + dc;
              if (
                newRow >= 0 && newRow < ROWS &&
                newCol >= 0 && newCol < COLS &&
                newBoard[newRow][newCol].isMine
              ) {
                count++;
              }
            }
          }
          newBoard[row][col].neighborCount = count;
        }
      }
    }

    setBoard(newBoard);
    setGameStatus('playing');
    setMineCount(MINES);
    setTime(0);
    setIsTimerRunning(false);
  }, []);

  useEffect(() => {
    initializeBoard();
  }, [initializeBoard]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerRunning && gameStatus === 'playing') {
      interval = setInterval(() => {
        setTime(prev => Math.min(prev + 1, 999));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, gameStatus]);

  const revealCell = (row: number, col: number) => {
    if (gameStatus !== 'playing' || board[row][col].isRevealed || board[row][col].isFlagged) {
      return;
    }

    if (!isTimerRunning) {
      setIsTimerRunning(true);
    }

    const newBoard = [...board];
    
    if (newBoard[row][col].isMine) {
      // Game over - reveal all mines
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          if (newBoard[r][c].isMine) {
            newBoard[r][c].isRevealed = true;
          }
        }
      }
      setGameStatus('lost');
      setIsTimerRunning(false);
    } else {
      // Reveal cell and potentially cascade
      const toReveal: [number, number][] = [[row, col]];
      const visited = new Set<string>();

      while (toReveal.length > 0) {
        const [r, c] = toReveal.pop()!;
        const key = `${r}-${c}`;
        
        if (visited.has(key) || newBoard[r][c].isRevealed || newBoard[r][c].isFlagged) {
          continue;
        }

        visited.add(key);
        newBoard[r][c].isRevealed = true;

        // If empty cell, reveal neighbors
        if (newBoard[r][c].neighborCount === 0) {
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              const newRow = r + dr;
              const newCol = c + dc;
              if (
                newRow >= 0 && newRow < ROWS &&
                newCol >= 0 && newCol < COLS &&
                !newBoard[newRow][newCol].isMine
              ) {
                toReveal.push([newRow, newCol]);
              }
            }
          }
        }
      }

      // Check for win condition
      let revealedCount = 0;
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          if (newBoard[r][c].isRevealed && !newBoard[r][c].isMine) {
            revealedCount++;
          }
        }
      }

      if (revealedCount === ROWS * COLS - MINES) {
        setGameStatus('won');
        setIsTimerRunning(false);
      }
    }

    setBoard(newBoard);
  };

  const toggleFlag = (e: React.MouseEvent, row: number, col: number) => {
    e.preventDefault();
    if (gameStatus !== 'playing' || board[row][col].isRevealed) {
      return;
    }

    const newBoard = [...board];
    newBoard[row][col].isFlagged = !newBoard[row][col].isFlagged;
    
    setMineCount(prev => newBoard[row][col].isFlagged ? prev - 1 : prev + 1);
    setBoard(newBoard);
  };

  const getCellContent = (cell: Cell) => {
    if (cell.isFlagged) return '🚩';
    if (!cell.isRevealed) return '';
    if (cell.isMine) return '💣';
    if (cell.neighborCount === 0) return '';
    return cell.neighborCount.toString();
  };

  const getCellClass = (cell: Cell) => {
    let className = 'minesweeper-cell';
    if (cell.isRevealed) {
      className += ' revealed';
      if (cell.isMine) {
        className += ' mine';
      } else if (cell.neighborCount > 0) {
        className += ` number-${cell.neighborCount}`;
      }
    }
    return className;
  };

  return (
    <div className="minesweeper-game">
      <div className="minesweeper-header">
        <div className="mine-counter">
          {String(mineCount).padStart(3, '0')}
        </div>
        <button 
          className="smiley-button"
          onClick={initializeBoard}
        >
          {gameStatus === 'won' ? '😎' : gameStatus === 'lost' ? '😵' : '🙂'}
        </button>
        <div className="timer">
          {String(time).padStart(3, '0')}
        </div>
      </div>
      <div className="minesweeper-board">
        {board.map((row, rowIndex) => (
          <div key={rowIndex} className="minesweeper-row">
            {row.map((cell, colIndex) => (
              <button
                key={`${rowIndex}-${colIndex}`}
                className={getCellClass(cell)}
                onClick={() => revealCell(rowIndex, colIndex)}
                onContextMenu={(e) => toggleFlag(e, rowIndex, colIndex)}
              >
                {getCellContent(cell)}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Minesweeper;